import React, { useCallback, useState } from "react";
import Cropper, { Area, MediaSize } from "react-easy-crop";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { selectionToPercentArea } from "./cropSpace";

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A crop rect together with the pixel space it was measured in. The
 * modal displays the optimized variant (capped at 1400px), not the
 * original the backend crops, so a bare rect is ambiguous — the backend
 * rescales it from sourceWidth/sourceHeight onto the actual original at
 * preparation time.
 */
export interface CropSelection extends CropRect {
  sourceWidth: number;
  sourceHeight: number;
}

interface MediaCropModalProps {
  isOpen: boolean;
  mediaItem: MediaItem | null;
  initialCrop?: CropSelection | null;
  onClose: () => void;
  onSave: (crop: CropSelection) => void;
  onClear?: () => void;
}

const ASPECT = 4 / 5;

export const MediaCropModal: React.FC<MediaCropModalProps> = ({
  isOpen,
  mediaItem,
  initialCrop,
  onClose,
  onSave,
  onClear,
}) => {
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  // The rect react-easy-crop reports is in the LOADED image's natural
  // pixel space; mediaSize records that space so the saved selection is
  // unambiguous for the backend rescale.
  const [pixelRect, setPixelRect] = useState<CropRect | null>(null);
  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);

  // Reset transient cropper state whenever we open with a different item.
  // react-easy-crop owns crop/zoom internally once mounted, so toggling
  // isOpen is enough — the Modal unmounts the body and the cropper resets.
  React.useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixelRect(null);
      setMediaSize(null);
    }
  }, [isOpen, mediaItem?.id, initialCrop]);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixelRect({
      x: Math.round(areaPixels.x),
      y: Math.round(areaPixels.y),
      width: Math.round(areaPixels.width),
      height: Math.round(areaPixels.height),
    });
  }, []);

  const onMediaLoaded = useCallback((size: MediaSize) => {
    setMediaSize(size);
  }, []);

  if (!mediaItem) return null;

  const imageUrl = mediaItem.getDisplayUrl();

  // Seed only from selections that carry their source space. Legacy
  // rects (pre-source-tracking) have unknown provenance — seeding them
  // would show a misleading frame, so the cropper starts centered.
  const initialPercentages = initialCrop
    ? selectionToPercentArea(initialCrop)
    : undefined;

  const handleSave = () => {
    if (pixelRect && mediaSize) {
      onSave({
        ...pixelRect,
        sourceWidth: mediaSize.naturalWidth,
        sourceHeight: mediaSize.naturalHeight,
      });
    }
    onClose();
  };

  const handleClear = () => {
    onClear?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Adjust 4:5 crop"
      size="xl"
      className="max-w-3xl"
    >
      <div className="flex flex-col">
        <div className="relative w-full bg-gray-900" style={{ height: 520 }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded}
            initialCroppedAreaPercentages={initialPercentages}
            objectFit="contain"
            restrictPosition
            showGrid
          />
        </div>
        <div className="px-6 pt-4 pb-2 flex items-center gap-3">
          <span className="text-sm text-gray-600 w-12">Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
          <span className="text-sm tabular-nums text-gray-500 w-12 text-right">
            {zoom.toFixed(2)}x
          </span>
        </div>
        <p className="px-6 pb-4 text-xs text-gray-500">
          Drag to reposition. The frame is locked to 4:5 — the same ratio used
          when publishing.
        </p>
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div>
            {initialCrop && onClear && (
              <Button variant="ghost" onClick={handleClear}>
                Reset to centered crop
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!pixelRect || !mediaSize}>
              Save crop
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
